import { useNavigate } from 'react-router-dom';

interface Plan {
  id: string;
  name: string;
  price: number;
  period: string;
  features: string[];
  popular?: boolean;
}

export default function Pricing() {
  const navigate = useNavigate();

  const plans: Plan[] = [
    {
      id: 'trial',
      name: 'Plan Prueba',
      price: 0,
      period: '30 días gratis',
      features: [
        'Durante el periodo de prueba, obtendrás acceso a todas las funciones sin limitaciones, incluyendo',
        'Creación de campañas de donación',
        'Publicación de casos de adopción',
        'Panel de administración completo',
        '¡Comienza hoy y sé parte del cambio que ellos necesitan!'
      ]
    },
    {
      id: 'premium',
      name: 'Plan Premium',
      price: 299,
      period: 'por mes',
      popular: true,
      features: [
        'Campañas de donación ilimitadas',
        'Casos de adopción ilimitados',
        'Panel de administración completo',
        'Integración con redes sociales',
        'Herramientas de marketing'
      ]
    }
  ];

  const handleGetStarted = () => {
    navigate('/register');
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex justify-between items-center">
            <div className="flex items-center">
              <img
                src="/images/image.png"
                alt="Huellitas Unidas Logo"
                className="w-10 h-10 object-contain mr-3"
              />
              <span className="text-xl font-bold text-gray-900">Huellitas Unidas</span>
            </div>
            <button 
              onClick={() => navigate('/')}
              className="text-gray-600 hover:text-gray-900 text-sm font-medium"
            >
              Volver al inicio
            </button>
          </div>
        </div>
      </div>

      {/* Hero Section */}
      <div className="bg-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Únete a Nuestra <span className="text-orange-500 font-extrabold">Comunidad</span>
            </h1>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto mb-12">
              Crea tu perfil de refugio y comienza a recaudar fondos para salvar más vidas. Miles de personas están esperando ayudar a tus perritos.
            </p>
            
            {/* Feature Icons */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-4xl mx-auto">
              <div className="text-center">
                <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                  </svg>
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Recauda Fondos</h3>
                <p className="text-gray-600 text-sm">Crea campañas de donación efectivas</p>
              </div>
              
              <div className="text-center">
                <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Facilita Adopciones</h3>
                <p className="text-gray-600 text-sm">Conecta mascotas con familias amorosas</p>
              </div>
              
              <div className="text-center">
                <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Gestiona Todo</h3>
                <p className="text-gray-600 text-sm">Administra tu refugio desde una plataforma</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Pricing Section */}
      <div className="bg-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Elige Tu <span className="text-orange-500">Plan</span>
            </h2>
            <p className="text-lg text-gray-600">
              Comienza gratis y escala según tus necesidades
            </p>
          </div>

          <div className="grid gap-8 md:grid-cols-2 max-w-4xl mx-auto mb-12">
            {plans.map((plan) => (
              <div
                key={plan.id}
                className={`border-2 rounded-xl p-8 bg-white flex flex-col h-full relative shadow-lg hover:shadow-xl transition-all duration-300 ${
                  plan.id === 'trial' ? 'border-blue-300 hover:border-blue-400' : 'border-gray-300 hover:border-gray-400'
                } ${plan.popular ? 'ring-2 ring-orange-500 border-orange-300' : ''}`}
              >
                {plan.popular && (
                  <div className="absolute -top-3 right-4">
                    <span className="bg-orange-500 text-white px-3 py-1 rounded-full text-xs font-medium">
                      Más Popular
                    </span>
                  </div>
                )}
                
                <div className="text-center mb-8">
                  <h3 className="text-xl font-semibold text-gray-900 mb-4">{plan.name}</h3>
                  <div className="text-3xl font-bold text-gray-900 mb-2">
                    {plan.price === 0 ? 'Gratis' : `$${plan.price}`}
                  </div>
                  <div className="text-sm text-gray-500">{plan.period}</div>
                </div>

                <div className="space-y-3 mb-8 flex-grow">
                  {plan.features.map((feature, index) => (
                    <div key={index} className="flex items-start">
                      <svg className="w-5 h-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      <span className="text-gray-700 text-sm">{feature}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>

          {/* Call to Action Button */}
          <div className="text-center mb-8">
            <button
              onClick={handleGetStarted}
              className="bg-orange-500 hover:bg-orange-600 text-white font-semibold py-4 px-8 rounded-lg transition-colors duration-300 flex items-center justify-center mx-auto"
            >
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
              </svg>
              Registrarse gratis
            </button>
            <p className="text-xs text-gray-500 mt-4">
              Sin compromisos - Cancela cuando quieras - Soporte incluido
            </p>
          </div>
        </div>
      </div>

      {/* Why Join Section */}
      <div className="bg-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              ¿Por qué unirse a <span className="text-orange-500">Huellitas Unidas</span>?
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Descubre todas las ventajas de formar parte de nuestra comunidad comprometida con el bienestar animal
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center border border-gray-300 rounded-xl p-6 bg-white shadow-md hover:shadow-lg transition-shadow duration-300">
              <div className="w-full h-48 rounded-lg mb-6 overflow-hidden border border-gray-200">
                <img
                  src="https://readdy.ai/api/search-image?query=Young%20woman%20working%20on%20laptop%20computer%20with%20a%20curious%20dog%20sitting%20beside%20her%20looking%20at%20the%20laptop%20screen%2C%20girl%20using%20technology%20with%20pet%20companion%2C%20modern%20workspace%20scene%20with%20dog%20watching%20computer%20screen%2C%20friendly%20interaction%20between%20person%20and%20dog%20while%20using%20laptop%2C%20cozy%20home%20office%20setting&width=400&height=300&seq=girl-laptop-dog-watching&orientation=landscape"
                  alt="Conecta con más corazones solidarios"
                  className="w-full h-full object-cover object-center"
                />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">
                Conecta con más corazones solidarios
              </h3>
              <p className="text-gray-600">
                Publica tus casos en Huellitas Unidas y recibe apoyo económico de personas comprometidas con el bienestar animal.
              </p>
            </div>

            <div className="text-center border border-gray-300 rounded-xl p-6 bg-white shadow-md hover:shadow-lg transition-shadow duration-300">
              <div className="w-full h-48 rounded-lg mb-6 overflow-hidden border border-gray-200">
                <img
                  src="https://readdy.ai/api/search-image?query=Heartwarming%20scene%20of%20people%20with%20big%20hearts%20helping%20rescued%20dogs%2C%20compassionate%20volunteers%20connecting%20with%20adorable%20puppies%2C%20emotional%20bond%20between%20humans%20and%20rescued%20pets%2C%20warm%20lighting%2C%20touching%20moment%20of%20solidarity%20and%20love%20for%20animals&width=400&height=300&seq=solidarity-hearts&orientation=landscape"
                  alt="Crea un perfil personalizado"
                  className="w-full h-full object-cover object-center"
                />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">
                Crea un perfil personalizado con toda la información de tu refugio
              </h3>
              <p className="text-gray-600">
                Gestiona todo desde una plataforma diseñada especialmente para refugios, con un entorno amigable, intuitivo y fácil de usar.
              </p>
            </div>

            <div className="text-center border border-gray-300 rounded-xl p-6 bg-white shadow-md hover:shadow-lg transition-shadow duration-300">
              <div className="w-full h-48 rounded-lg mb-6 overflow-hidden border border-gray-200">
                <img
                  src="https://readdy.ai/api/search-image?query=Animal%20shelter%20organizing%20community%20events%20and%20activities%2C%20adoption%20fair%20with%20volunteers%20and%20families%2C%20vaccination%20campaigns%20for%20rescued%20dogs%2C%20fundraising%20events%20for%20animal%20welfare%2C%20community%20gathering%20supporting%20pet%20rescue%20shelter%20with%20banners%20and%20promotional%20activities&width=400&height=300&seq=shelter-events-promotion&orientation=landscape"
                  alt="Publica y promociona actividades"
                  className="w-full h-full object-cover object-center"
                />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">
                Publica y promociona las actividades de tu refugio
              </h3>
              <p className="text-gray-600">
                Organiza jornadas de adopción, campañas de vacunación, colectas o eventos solidarios.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
